

/**
 * Theme for being used in the checkout and store integration.
 */

import { colors, palette } from 'ui/themes/colors'

// DO NOT USE THESE COLORS! THEY ARE DEPRECATED AND WILL BE REMOVED IN THE FUTURE
const deprecatedColors = (brandingColor) => {
  return {
    primary: brandingColor,
    secondary: colors['Text/Secondary'],
    success: colors['Success/Base'],
    info: colors['Accent1/Base'],
    warning: colors['Warning/Base'],
    danger: colors['Danger/Base'],

    light: palette.grey[20],
    dark: colors['Text/Primary'],

    borderHover: palette.grey[40]
  }
}

const color = (brandingColor) => {
  return {
    black: palette.black,
    white: palette.white,
    transparent: 'transparent',
    branding: brandingColor,

    // Ease of use colors, corresponding to the Base color of the palette.
    // e.g. blue =~ colors['Primary/Base']
    blue: palette.blue[60],
    green: palette.green[50],
    orange: palette.orange[50],
    red: palette.red[60],
    purple: palette.purple[60],
    grey: palette.grey[30],

    ...colors,
    ...deprecatedColors(brandingColor)
  }
}

/**
 * We use a 4px grid system for spacing. This function calculates the pixel value for a given grid unit.
 * 0.5 grid units = 2px
 * 1 grid unit = 4px
 * 2 grid units = 8px
 * 3 grid units = 12px
 * 4 grid units = 16px
 * 5 grid units = 20px
 * 6 grid units = 24px
 * 7 grid units = 28px
 * 8 grid units = 32px
 * 9 grid units = 36px
 * 10 grid units = 40px
 * 11 grid units = 44px
 * 12 grid units = 48px
 * 16 grid units = 64px
 */
const grid = (unit) => {
  if (!Number.isInteger(unit) && unit !== 0.5) {
    throw new Error('Grid units must be integers or 0.5')
  }
  return `${unit * 4}px`
}

const units = {
  0.5: grid(0.5),
  1: grid(1),
  2: grid(2),
  3: grid(3),
  4: grid(4),
  5: grid(5),
  6: grid(6),
  7: grid(7),
  8: grid(8),
  9: grid(9),
  10: grid(10),
  11: grid(11),
  12: grid(12),
  13: grid(13),
  14: grid(14),
  15: grid(15),
  16: grid(16)
}

const font = {
  base: 'Roboto',
  fallback: 'sans-serif',
  mono: 'Monospace',
  fontAwesomeStyle: 'far',
  size: {
    body: '20px',
    xs: '13px !important',
    sm: '13px',
    md: '14px',
    lg: '18px',
    lgplus: '22px',
    xl: '34px',
    xxl: '50px',
    xxxl: '66px'
  },
  weight: {
    light: '300',
    lighter: '400',
    normal: '500',
    semibold: '600',
    bold: '700'
  }
}

font.body = `${font.base}, ${font.fallback}`

const size = {
  borderRadius: {
    xs: '2px',
    sm: '6px',
    md: '6px',
    lg: '6px',
    lgplus: '8px',
    xl: '13px'
  },
  borderWidth: {
    xs: '1px',
    sm: '1px',
    md: '1px',
    lg: '1px'
  },
  controlHeight: {
    xs: '26px',
    sm: '36px',
    md: '42px',
    lg: '50px'
  },
  margin: {
    xs: grid(1),
    sm: grid(3),
    md: grid(4),
    lg: grid(6)
  },
  padding: {
    xs: grid(1),
    sm: grid(3),
    md: grid(4),
    mdplus: grid(5),
    lg: grid(6),
    lgplus: grid(7),
    xl: grid(8),
    xlplus: grid(11),
    xxl: grid(12),
    xxxl: grid(16)
  },
  shadow: {
    sm: 'rgba(0, 0, 0, 0.2) 0px 1px 2px 0px',
    smplus: 'rgba(0, 0, 0, 0.1) 0px 4px 12px 0px',
    md: 'rgba(0, 0, 0, 0.1) 0 6px 14px 0',
    lg: 'rgba(0, 0, 0, 0.1) 0 6px 14px 0'
  },
  screenSizes: {
    xs: '0px',
    sm: '768px',
    md: '992px',
    lg: '1200px',
    xl: '1800px'
  }
}

const opacity = {
  disabled: 0.6,
  destroying: 0.4
}

const zIndex = [
  1,
  100,
  200,
  300,
  400
]

export default (brandingColor = '#8fcc40') => {
  return {
    color: color(brandingColor),
    font,
    opacity,
    size,
    zIndex,
    units
  }
}
